import { useState, useEffect } from 'react';
import { TaggedJourney } from '../utils/mapJourneysData';
import { SearchState, RecentSearch } from '../types/search';

/**
 * Hook to manage loading initial state from localStorage
 */
export const useLocalStorage = () => {
  const [journeys, setJourneys] = useState<TaggedJourney[]>([]);
  const [earlierThan, setEarlierThan] = useState<string | null>(null);
  const [laterThan, setLaterThan] = useState<string | null>(null);
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);

  useEffect(() => {
    const storedJourneys = JSON.parse(localStorage.getItem('journeys') || '[]');
    if (storedJourneys.length > 0) {
      setJourneys(storedJourneys);
    }

    const storedEarlierThan = JSON.parse(localStorage.getItem('earlierThan') || '[]');
    if (storedEarlierThan.length > 0) {
      setEarlierThan(storedEarlierThan);
    }

    const storedLaterThan = JSON.parse(localStorage.getItem('laterThan') || '[]');
    if (storedLaterThan.length > 0) {
      setLaterThan(storedLaterThan);
    }

    // Load recent searches from localStorage
    // Cast tripType to union type since JSON.parse returns strings
    const storedSearches = JSON.parse(localStorage.getItem('recentSearches') || '[]') as RecentSearch[];
    // Ensure tripType is properly typed
    const typedSearches = storedSearches.map(search => ({
      ...search,
      tripType: (search.tripType === 'departure' || search.tripType === 'arrival' 
        ? search.tripType 
        : 'departure') as 'departure' | 'arrival'
    }));
    setRecentSearches(typedSearches);
  }, []);

  return { journeys, setJourneys, earlierThan, setEarlierThan, laterThan, setLaterThan, recentSearches, setRecentSearches };
};

/**
 * Load search state from localStorage (only if URL doesn't have params)
 */
export const loadSearchStateFromLocalStorage = (): Partial<SearchState> | null => {
  // Check if URL has params - if so, don't load from localStorage as URL takes precedence
  const urlHasParams = window.location.search.includes('from=') && window.location.search.includes('to=');
  
  if (urlHasParams) {
    return null;
  }

  const storedOrigin = localStorage.getItem('originId') || '';
  const storedDestination = localStorage.getItem('destinationId') || '';
  
  const options = Object.keys(localStorage).reduce((acc, key) => {
    if (key.startsWith('option_')) {
      const optionKey = key.substring('option_'.length);
      acc[optionKey] = JSON.parse(localStorage.getItem(key) ?? 'null');
    }
    return acc;
  }, {} as Record<string, any>);

  return {
    originId: storedOrigin,
    destinationId: storedDestination,
    originName: '',
    destinationName: '',
    date: localStorage.getItem('localDate') || '',
    tripType: (localStorage.getItem('localTripType') as 'departure' | 'arrival') || 'departure',
    options,
  };
};

/**
 * Save a search to recent searches in localStorage
 */
export const saveSearchToRecent = (
  originId: string,
  originName: string,
  destinationId: string,
  destinationName: string,
  date: string,
  tripType: 'departure' | 'arrival',
  options: Record<string, any>,
  setRecentSearches: React.Dispatch<React.SetStateAction<RecentSearch[]>>
) => {
  const searchDate = date || new Date().toISOString();
  
  // Create a search record with proper typing
  const searchRecord = { 
    originId, 
    originName, 
    destinationId, 
    destinationName, 
    date: searchDate, 
    tripType: tripType as 'departure' | 'arrival', 
    options, 
    timestamp: new Date().toISOString() 
  };

  // Get existing searches and ensure proper typing
  const existingSearches = JSON.parse(localStorage.getItem('recentSearches') || '[]') as RecentSearch[];
  const typedExistingSearches = existingSearches.map(search => ({
    ...search,
    tripType: (search.tripType === 'departure' || search.tripType === 'arrival' 
      ? search.tripType 
      : 'departure') as 'departure' | 'arrival'
  }));

  // Keep more items in storage (e.g., last 10) to ensure we can get 5 unique combinations
  const updatedSearches = typedExistingSearches.slice(-10);
  
  // Add the new search record
  updatedSearches.push(searchRecord);
  
  // Save updated searches back to localStorage
  localStorage.setItem('recentSearches', JSON.stringify(updatedSearches));
  
  // Update the state
  setRecentSearches(updatedSearches);
};
